import React, { useState } from 'react';
import { DialogClose } from '@/components/atoms/Dialog';
import { Button } from '@/components/atoms/NewPcButton';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import { CPLAT_API_ROUTES, usePost } from '@/api';
import { SELLERBOX_PLUS_MONTHLY_AMOUNT } from '@/constants/subscription';

// {
//   "cplatToken": "string",
//   "subscriptionNo": 0,
//   "cardNo": "string",
//   "expirationDate": "string",
//   "password": "string",
//   "cardOwnerInfo": "string",
//   "promotionCode": "string",
//   "alreadyPaidInfo": {
//     "appleTransactionId": "string",
//     "appleResponseJson": "string",
//     "googleTransactionId": "string",
//     "googleResponseJson": "string"
//   }
// }

interface ChangeBillCardProps {
  isPrePayment?: boolean;
  subscriptionNo: number;
  paymentAmount: number;
  setBillDialogOpen: (open: boolean) => void;
}

const ChangeBillCard = ({
  isPrePayment,
  subscriptionNo,
  paymentAmount,
  setBillDialogOpen,
}: ChangeBillCardProps) => {
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardPassword, setCardPassword] = useState('');
  const [cardBirth, setCardBirth] = useState('');
  const { showToast } = useToast();
  const { CplatToken } = useAppSelector((state) => state.user);

  // 카드 결제(구독 등록) API
  const { mutate: requestCardPay, isPending: isLoading } = usePost(
    CPLAT_API_ROUTES.setSubscription,
  );

  // 카드번호: 숫자만, 최대 16자리
  const handleCardNumber = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '').slice(0, 16);
    setCardNumber(onlyNums);
  };

  // 유효기간: MM/YY 형식, 숫자만 4자리 입력 후 MM/YY로 변환
  const handleCardExpiry = (e: React.ChangeEvent<HTMLInputElement>) => {
    let onlyNums = e.target.value.replace(/[^0-9]/g, '').slice(0, 4);
    if (onlyNums.length >= 3) {
      onlyNums = onlyNums.slice(0, 2) + '/' + onlyNums.slice(2);
    }
    setCardExpiry(onlyNums);
  };

  // 비밀번호 앞 2자리: 숫자만, 최대 2자리, type="password"
  const handleCardPassword = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '').slice(0, 2);
    setCardPassword(onlyNums);
  };

  // 생년월일/사업자번호: 숫자만, 최대 10자리
  const handleCardBirth = (e: React.ChangeEvent<HTMLInputElement>) => {
    const onlyNums = e.target.value.replace(/[^0-9]/g, '').slice(0, 10);
    setCardBirth(onlyNums);
  };

  // MM/YY → YYMM 변환 (예: 08/26 → 2608)
  const getExpiryYYMM = (mmYY: string) => {
    const [mm, yy] = mmYY.split('/');
    if (!mm || !yy) return '';
    return yy + mm;
  };

  const handleCardPay = () => {
    // 유효성 검사 (간단 예시, 필요에 따라 보강)
    if (cardNumber.length !== 16) {
      showToast('warning', '카드번호 16자리를 입력해주세요.');
      return;
    }
    if (!/^\d{2}\/\d{2}$/.test(cardExpiry)) {
      showToast('warning', '유효기간을 MM/YY 형식으로 입력해주세요.');
      return;
    }
    if (cardPassword.length !== 2) {
      showToast('warning', '카드 비밀번호 앞 2자리를 입력해주세요.');
      return;
    }
    if (cardBirth.length !== 6 && cardBirth.length !== 10) {
      showToast('warning', '생년월일 6자리 또는 사업자번호 10자리를 입력해주세요.');
      return;
    }
    if (!CplatToken) {
      showToast('warning', '로그인이 필요합니다.');
      return;
    }

    requestCardPay(
      {
        cplatToken: CplatToken,
        subscriptionNo: subscriptionNo, // 실제 상품 번호로 대체
        isPrePayment,
        cardNo: cardNumber,
        expirationDate: getExpiryYYMM(cardExpiry), // YYMM
        password: cardPassword,
        cardOwnerInfo: cardBirth,
      },
      {
        onSuccess: (res) => {
          if (res.data.code === '200') {
            showToast(
              'congratulations',
              '멤버십 구독이 완료되었어요! 지금 바로 셀러박스 플러스 기능을 이용해 보세요.',
            );
            setBillDialogOpen(false); // 다이얼로그 닫기
            // 필요하다면 구독정보 재조회 등 추가
          } else {
            showToast('warning', res.data.message || '결제에 실패했습니다. 다시 시도해주세요.');
          }
        },
        onError: () => {
          showToast('warning', '결제에 실패했습니다. 다시 시도해주세요.');
        },
      },
    );
  };

  return (
    <div className="flex flex-col ">
      <ItemLayout>
        <Label>카드번호</Label>
        <div className="w-full">
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={19}
            placeholder="숫자만 입력해 주세요"
            className="w-full rounded-[8px] border border-gr-300 px-[10px] py-[12px] text-gr-700 Body5M14 focus-within:ring-2 focus-within:ring-point-500 focus:outline-none"
            value={cardNumber.replace(/(.{4})/g, '$1 ').trim()} // 4자리마다 공백
            onChange={handleCardNumber}
            autoComplete="cc-number"
          />
        </div>
      </ItemLayout>
      <div className="mt-[12px] flex gap-5">
        <ItemLayout>
          <Label>유효기간</Label>
          <div className="w-full">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9/]*"
              maxLength={5}
              placeholder="MM/YY"
              className="w-full rounded-[8px] border border-gr-300 px-[10px] py-[12px] text-gr-700 Body5M14 focus-within:ring-2 focus-within:ring-point-500 focus:outline-none"
              value={cardExpiry}
              onChange={handleCardExpiry}
              autoComplete="cc-exp"
            />
          </div>
        </ItemLayout>
        <ItemLayout>
          <Label>비밀번호 앞 2자리</Label>
          <div className="w-full">
            <input
              type="password"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={2}
              placeholder="**"
              className="w-full rounded-[8px] border border-gr-300 px-[10px] py-[12px] text-gr-700 Body5M14 focus-within:ring-2 focus-within:ring-point-500 focus:outline-none"
              value={cardPassword}
              onChange={handleCardPassword}
              autoComplete="off"
            />
          </div>
        </ItemLayout>
      </div>
      <div className="mt-[12px] w-full">
        <ItemLayout>
          <Label>생년월일 6자리(개인) / 사업자번호 10자리(법인)</Label>
          <input
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={10}
            placeholder="숫자만 입력해주세요"
            className="w-full rounded-[8px] border border-gr-300 px-[10px] py-[12px] text-gr-700 Body5M14 focus-within:ring-2 focus-within:ring-point-500 focus:outline-none"
            value={cardBirth}
            onChange={handleCardBirth}
            autoComplete="off"
          />
        </ItemLayout>
      </div>
      <div className="mt-[10px] flex w-full items-center justify-end gap-5">
        <div className="flex flex-col items-end">
          <div className="text-gr-900 Body1S16">
            결제 금액 {paymentAmount.toLocaleString()}원
          </div>
          {isPrePayment && (
            <div className="mt-[2px] text-gr-500 Caption1M12">
              14일 무료 체험 이후 {SELLERBOX_PLUS_MONTHLY_AMOUNT.toLocaleString()}원이 자동
              결제됩니다
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <DialogClose asChild>
            <Button size="small" variant="line" className="h-[38px] shadow-none">
              취소
            </Button>
          </DialogClose>
          <Button isLoading={isLoading} size="small" onClick={handleCardPay} disabled={isLoading}>
            다음
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChangeBillCard;

const Label = ({ children }: { children: React.ReactNode }) => {
  return <div className="text-gr-800 Caption1M12">{children}</div>;
};

const ItemLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="flex w-full flex-1 flex-col items-start justify-start gap-2">{children}</div>
  );
};
